import React from "react";

import { AnchorTag, FlexComp, FormattedText, SpanTag } from "@tm/static";
import { theme } from "@tm/utility/global";
import type {
  DynamicFieldConfig,
  FieldConfig,
  FormConfig,
  FormDataItem,
} from "../../types";
import AddressInput from "../AddressInput";
import FormInput from "../FormInput";
import FormSelect from "../FormSelect";
import ModalRadioButton from "../ModalRadioButton";
import {
  AddressFieldWrapper,
  DateFieldWrapper,
  DynamicFieldTrigger,
  ErrorBanner,
  ErrorIcon,
  FieldItem,
  FieldsFooter,
  FieldsRow,
  GenderSection,
  GenderTypeItem,
  RadioFieldWrapper,
} from "./FormRenderer.style";
import { maxDate, minDate, validateField } from "./formUtils";

// ── Props ───────────────────────────────────────────────────────────────

interface FormRendererFieldsProps {
  entityIndex: number;
  entityData: FormDataItem;
  entityErrors: Record<string, string>;
  formConfig: FormConfig;
  getFieldRef: (
    entityIndex: number,
    fieldKey: string
  ) => React.RefObject<HTMLInputElement>;
  handleFieldChange: (
    entityIndex: number,
    fieldKey: string,
    value: string
  ) => void;
  handleMaskedFocus: (entityIndex: number, fieldKey: string) => void;
  handleMaskedBlur: (
    entityIndex: number,
    fieldKey: string,
    currentValue: string,
    field: FieldConfig
  ) => void;
  handleDynamicFieldChange: (
    entityIndex: number,
    triggerId: string,
    fieldKey: string,
    value: string
  ) => void;
  toggleDynamicField: (entityIndex: number, triggerId: string) => void;
  setErrors: React.Dispatch<
    React.SetStateAction<Record<number, Record<string, string>>>
  >;
}

// ── Radio field sub-component ───────────────────────────────────────────

const RadioField: React.FC<{
  field: FieldConfig;
  entityIndex: number;
  entityData: FormDataItem;
  handleFieldChange: FormRendererFieldsProps["handleFieldChange"];
}> = ({ field, entityIndex, entityData, handleFieldChange }) => {
  const fieldKey = field.name;

  return (
    <RadioFieldWrapper>
      <GenderSection>
        <SpanTag
          margin="0 0 8px"
          fontSize="md"
          fontWeight="bold"
          color={theme.greyText}
        >
          <FormattedText value={field.label} />
          {field.isRequired && (
            <SpanTag fontWeight="bold" color={theme.redText2}>
              *
            </SpanTag>
          )}
        </SpanTag>
        <FlexComp gap="8px" margin="0">
          {field.options?.map((option) => {
            const currentValue = String(entityData[fieldKey] || "").trim();
            const optionValue = String(option.value || "").trim();
            const isSelected =
              currentValue.toUpperCase() === optionValue.toUpperCase();
            const uniqueId = `${fieldKey}-${entityIndex}-${option.id}`;
            const uniqueName = `${fieldKey}-${entityIndex}`;

            return (
              <GenderTypeItem
                key={option.id}
                selected={isSelected}
                onClick={() => {
                  if (field.isEnabled) {
                    handleFieldChange(entityIndex, fieldKey, option.value);
                  }
                }}
              >
                <ModalRadioButton
                  activeBtn={isSelected}
                  inputId={uniqueId}
                  name={uniqueName}
                >
                  <SpanTag
                    fontSize="sm"
                    fontWeight="bold"
                    color={
                      isSelected ? `var(--tm-accent-color)` : theme.greyText
                    }
                  >
                    {option.label}
                  </SpanTag>
                </ModalRadioButton>
              </GenderTypeItem>
            );
          })}
        </FlexComp>
      </GenderSection>
    </RadioFieldWrapper>
  );
};

// ── Dynamic fields sub-component ────────────────────────────────────────

const DynamicFieldsSection: React.FC<{
  dynamicField: DynamicFieldConfig;
  entityIndex: number;
  entityData: FormDataItem;
  entityErrors: Record<string, string>;
  getFieldRef: FormRendererFieldsProps["getFieldRef"];
  handleDynamicFieldChange: FormRendererFieldsProps["handleDynamicFieldChange"];
  toggleDynamicField: FormRendererFieldsProps["toggleDynamicField"];
  setErrors: FormRendererFieldsProps["setErrors"];
}> = ({
  dynamicField: df,
  entityIndex,
  entityData,
  entityErrors,
  getFieldRef,
  handleDynamicFieldChange,
  toggleDynamicField,
  setErrors,
}) => {
  const isVisible = entityData.dynamicFields?.[df.triggerId]?.visible || false;
  if (!isVisible) return null;

  return (
    <AddressFieldWrapper>
      {df.fields.map((field) => {
        const fieldKey = field.name;
        const fieldError = entityErrors[`${df.triggerId}_${fieldKey}`];
        const fieldValue = String(
          (entityData.dynamicFields?.[df.triggerId]?.[fieldKey] as
            | string
            | undefined) || ""
        );

        return (
          <AddressInput
            key={fieldKey}
            ref={getFieldRef(entityIndex, `${df.triggerId}_${fieldKey}`)}
            labelTxt={field.label}
            placeholderTxt={field.placeholder}
            requiredField={field.isRequired}
            inputValue={fieldValue}
            errorState={fieldError}
            hasError={!!fieldError}
            isDisabled={!field.isEnabled}
            alwaysShowClearButton={true}
            handleChange={(e) => {
              handleDynamicFieldChange(
                entityIndex,
                df.triggerId,
                fieldKey,
                e.target.value
              );
            }}
            handleBlur={(e) => {
              const error = validateField(e.target.value, field);
              setErrors((prev) => ({
                ...prev,
                [entityIndex]: {
                  ...prev[entityIndex],
                  [`${df.triggerId}_${fieldKey}`]: error,
                },
              }));
            }}
            onClear={() => {
              handleDynamicFieldChange(entityIndex, df.triggerId, fieldKey, "");
              toggleDynamicField(entityIndex, df.triggerId);
            }}
            margin="0 0 16px"
          />
        );
      })}
    </AddressFieldWrapper>
  );
};

// ── Select field helper ─────────────────────────────────────────────────

const SelectField: React.FC<{
  field: FieldConfig;
  entityIndex: number;
  displayValue: string;
  entityErrors: Record<string, string>;
  handleFieldChange: FormRendererFieldsProps["handleFieldChange"];
  setErrors: FormRendererFieldsProps["setErrors"];
}> = ({
  field,
  entityIndex,
  displayValue,
  entityErrors,
  handleFieldChange,
  setErrors,
}) => {
  const fieldKey = field.name;
  return (
    <FieldItem>
      <FormSelect
        flex="1"
        labelTxt={field.label}
        placeholderTxt={field.placeholder}
        requiredField={field.isRequired}
        selectValue={displayValue}
        errorState={entityErrors[fieldKey]}
        hasError={!!entityErrors[fieldKey]}
        showError={false}
        isDisabled={!field.isEnabled}
        options={field.options || []}
        handleChange={(e) => {
          handleFieldChange(entityIndex, fieldKey, e.target.value);
        }}
        handleBlur={(e) => {
          const error = validateField(e.target.value, field);
          setErrors((prev) => ({
            ...prev,
            [entityIndex]: {
              ...prev[entityIndex],
              [fieldKey]: error,
            },
          }));
        }}
        margin="0"
      />
    </FieldItem>
  );
};

// ── Main component ──────────────────────────────────────────────────────

export const FormRendererFields: React.FC<FormRendererFieldsProps> = ({
  entityIndex,
  entityData,
  entityErrors,
  formConfig,
  getFieldRef,
  handleFieldChange,
  handleMaskedFocus,
  handleMaskedBlur,
  handleDynamicFieldChange,
  toggleDynamicField,
  setErrors,
}) => {
  const hasErrors = Object.values(entityErrors).some((e) => e);
  const visibleFields = formConfig.fields.filter((field) => field.isVisible);

  return (
    <>
      <FieldsRow>
        {visibleFields.map((field) => {
          const fieldKey = field.name;
          const displayValue = String(entityData[fieldKey] || "");

          // RADIO
          if (field.type === "RADIO" && field.options) {
            return (
              <RadioField
                key={fieldKey}
                field={field}
                entityIndex={entityIndex}
                entityData={entityData}
                handleFieldChange={handleFieldChange}
              />
            );
          }

          // SELECT
          if (field.type === "SELECT") {
            return (
              <SelectField
                key={fieldKey}
                field={field}
                entityIndex={entityIndex}
                displayValue={displayValue}
                entityErrors={entityErrors}
                handleFieldChange={handleFieldChange}
                setErrors={setErrors}
              />
            );
          }

          // DATE
          const isDateField = field.type === "DATE";
          if (isDateField) {
            return (
              <DateFieldWrapper key={fieldKey}>
                <FormInput
                  ref={getFieldRef(entityIndex, fieldKey)}
                  flex="1"
                  labelTxt={field.label}
                  placeholderTxt={field.placeholder}
                  requiredField={field.isRequired}
                  inputValue={displayValue}
                  errorState={entityErrors[fieldKey]}
                  hasError={!!entityErrors[fieldKey]}
                  showError={false}
                  isDisabled={!field.isEnabled}
                  inputType="date"
                  minValue={minDate}
                  maxValue={maxDate}
                  handleChange={(e) => {
                    handleFieldChange(entityIndex, fieldKey, e.target.value);
                  }}
                  handleBlur={(e) => {
                    const error = validateField(e.target.value, field);
                    setErrors((prev) => ({
                      ...prev,
                      [entityIndex]: {
                        ...prev[entityIndex],
                        [fieldKey]: error,
                      },
                    }));
                  }}
                  margin="0"
                />
              </DateFieldWrapper>
            );
          }

          // TEXT (default)
          return (
            <FieldItem key={fieldKey}>
              <FormInput
                ref={getFieldRef(entityIndex, fieldKey)}
                flex="1"
                labelTxt={field.label}
                placeholderTxt={field.placeholder}
                requiredField={field.isRequired}
                inputValue={displayValue}
                errorState={entityErrors[fieldKey]}
                hasError={!!entityErrors[fieldKey]}
                showError={false}
                isDisabled={!field.isEnabled}
                inputType="text"
                handleChange={(e) => {
                  handleFieldChange(entityIndex, fieldKey, e.target.value);
                }}
                handleFocus={
                  field.isMasked
                    ? () => handleMaskedFocus(entityIndex, fieldKey)
                    : undefined
                }
                handleBlur={(e) => {
                  if (field.isMasked) {
                    handleMaskedBlur(
                      entityIndex,
                      fieldKey,
                      e.target.value,
                      field
                    );
                  } else {
                    const error = validateField(e.target.value, field);
                    setErrors((prev) => ({
                      ...prev,
                      [entityIndex]: {
                        ...prev[entityIndex],
                        [fieldKey]: error,
                      },
                    }));
                  }
                }}
                margin="0"
              />
            </FieldItem>
          );
        })}
      </FieldsRow>

      {/* Dynamic field triggers */}
      {formConfig.dynamicFields?.some(
        (df) =>
          df.trigger.isVisible &&
          !entityData.dynamicFields?.[df.triggerId]?.visible
      ) && (
        <FieldsFooter>
          {formConfig.dynamicFields?.map((df) => {
            if (!df.trigger.isVisible) return null;
            const isVisible =
              entityData.dynamicFields?.[df.triggerId]?.visible || false;
            if (isVisible) return null;

            return (
              <DynamicFieldTrigger key={df.triggerId}>
                <AnchorTag
                  fontWeight="bold"
                  handleClick={() => {
                    toggleDynamicField(entityIndex, df.triggerId);
                  }}
                >
                  <FormattedText value={df.trigger.label} />
                </AnchorTag>
              </DynamicFieldTrigger>
            );
          })}
        </FieldsFooter>
      )}

      {/* Expanded dynamic fields */}
      {formConfig.dynamicFields?.map((df) => {
        if (!df.trigger.isVisible) return null;
        return (
          <DynamicFieldsSection
            key={df.triggerId}
            dynamicField={df}
            entityIndex={entityIndex}
            entityData={entityData}
            entityErrors={entityErrors}
            getFieldRef={getFieldRef}
            handleDynamicFieldChange={handleDynamicFieldChange}
            toggleDynamicField={toggleDynamicField}
            setErrors={setErrors}
          />
        );
      })}

      {/* Error banner */}
      {hasErrors && (
        <ErrorBanner>
          <ErrorIcon>⚠</ErrorIcon>
          <SpanTag fontSize="xs" color={theme.redText}>
            {Object.values(entityErrors)
              .filter((e) => e)
              .join(". ")}
          </SpanTag>
        </ErrorBanner>
      )}
    </>
  );
};
